import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Calendar, Tooltip, Divider, Row, Col } from "antd";
import moment from "moment";
import React, { useMemo } from "react";
import styled, { css } from "styled-components";

import { useFetcher } from "@/modules/dataFetcher";
import { Field } from "@/modules/forms/Blocks";
import { DAYS_OF_WEEK } from "@/utils/date";
import media from "@/utils/media";
import { useFormContext } from "@/modules/forms";

import { SubTitle } from "../styled/text";
import {
  appointmentForm,
  invalidTimeFetcher,
  openTimeFetcher,
} from "./modules";
import PanelToggle from "./PanelToggle";
import { useState } from "react";
import SectionHeader from "../common/SectionHeader";

const ShortDayWrap = styled.div`
  background-color: #a0a0a0;
  width: 6px;
  height: 6px;
  position: absolute;
  top: 3px;
  left: 50%;
  margin-left: -3px;
  z-index: 100;
  border-radius: 3px;
  border: 1px solid #ddd;
`;

const DatePickerWrap = styled.div`
  width: 288px;
  height: 312px;
  max-width: 100%;
  background-color: #fff;
  margin: auto;
  padding: 24px;
  border-radius: 15px;
  margin-bottom: 8px;
  .ant-picker-calendar-header {
    padding-left: 4px;
  }
  box-shadow: 0px 4px 6px rgb(0 0 0 / 10%), 0px 0px 6px rgb(0 0 0 / 15%);
  .ant-select-single.ant-select-sm:not(.ant-select-customize-input) .ant-select-selector {
    border: 1px solid var(--primary-main-color);
    border-radius: 8px;
  }
  .ant-fullcalendar-value {
    width: 32px;
    height: 32px;
    line-height: 32px;
  }
  header {
    height: 71px;
    display: flex;
    align-items: center;
    border: 1px solid #C0C0C0;
    margin: 0 -20px 10px;
    padding: 0 20px;
  }

  h4 {
    margin-bottom: 0;
  }

  &&&&& .ant-select {
    width: auto;
  }

  .ant-radio-group {
    display: none;
  }
  .ant-picker-content th {
    text-transform: uppercase;
    color: #909090;
  }
  .ant-fullcalendar-date {
    position: relative;
  }
  .ant-picker-cell-disabled, .ant-picker-cell-disabled:before{
    background-color: #fff;
  }
  .ant-picker-cell-selected .ant-picker-calendar-date-value{
    background-color: none;
    font-weight: bold;
  }
  .ant-picker-calendar-date-value{
    font-weight: bold;
  }
  .ant-picker-cell-selected .ant-picker-calendar-date{
    background-color: var(--primary-main-color) !important;
    background: var(--primary-main-color) !important;
    border: 1px solid  var(--primary-main-color);
    border-radius: 5px;
  }
  .ant-fullcalendar-selected-day {
    ${ShortDayWrap} {
      background-color: #fff;
    }
  }

  .ant-fullcalendar-content {
    position: static;
  }
  .ant-picker-cell, .ant-picker-cell-in-view {
    width: 32px;
    height : 32px;
    padding:0;
    margin: 0;
  }
  .ant-picker-content td,  .ant-picker-content th {
    width: 32px !important;
    height: 32px !important;
    // margin-right: 100px !important;
  }
  .ant-picker-content th {
    font-size: 8px;
  }
  
  .ant-picker-content tr {
    display: flex;
    gap: 4px;
    height: 32px !important;
    line-height: 32px !important;
  }
  .ant-picker-panel {
    border-top: none;
  }
  .ant-picker-calendar-header {
    padding-top: 0px;
    padding-bottom: 0px;
  }
  .ant-picker-calendar-date-value {
    font-size: 12px;
  }
  .ant-fullcalendar-value {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    line-height: 32px;
    font-size: 12px;
    font-weight: 700;
  }

  .ant-fullcalendar-selected-day .ant-fullcalendar-value {
    color: white;
  }

  .ant-fullcalendar-next-month-btn-day:not(.ant-fullcalendar-disabled-cell)
    .ant-fullcalendar-value {
    color: rgba(0, 0, 0, 0.65);
  }

  .ant-fullcalendar {
    border: none;

    th {
      line-height: 32px;
      // border-top: 1px solid #ddd;
      border: 1px solid  #C0C0C0;
      color: #a0a0a0;
      font-size: 11px;
    }
  }

  box-shadow: none;
  margin: unset;
  border: 1px solid  #C0C0C0;

  header {
    margin: 0 -41px 20px;
  }
`;

function CalendarField({ value, onChange }) {
  const invalidTimes = useFetcher({ dataFetcher: invalidTimeFetcher });
  const openedTimes = useFetcher({ dataFetcher: openTimeFetcher });

  const shortDays = useMemo(() => {
    if (!invalidTimes.data) {
      return [];
    }
    return invalidTimes.data.map((time) => ({
      date: moment(time.checkDay),
      scheduele: time.open_close_time,
      reason: time.reason,
    }));
  }, [invalidTimes.data]);

  return (
    <Calendar
      fullscreen={false}
      // mode="year"
      disabledDate={(date) => {
        const now = moment();
        const isBefore = date.isBefore(now);
        if (isBefore) {
          return true;
        }
        const day = date.isoWeekday();

        const isClosed =
          !openedTimes?.data?.[DAYS_OF_WEEK[day]] ||
          ["gesloten", "closed"].includes(
            openedTimes?.data?.[DAYS_OF_WEEK[day]]?.toLowerCase()
          );
        return isClosed;
      }}
      onSelect={(value) => onChange(moment(value).toString())}
      onPanelChange={(value) => onChange(moment(value).toString())}
      dateCellRender={(date) => {
        const short = shortDays.find((shortDay) =>
          date.isSame(shortDay.date, "day")
        );
        if (!short) {
          return null;
        }
        return (
          <Tooltip title={short.reason}>
            <ShortDayWrap />
          </Tooltip>
        );
      }}
    />
  );
}

const TimeOption = styled.div`
  display: flex;
  min-width: 72px;
  // max-width: 72px;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  // max-height: 30px;
  background: #FFFFFF;
  border: 1px solid #C0C0C0;
  border-radius: 8px;
  font-weight: bold !important;
  justify-content: center;
  font-size: 16px;
  letter-spacing: 1px;
  color: #707070;
  font-weight: 700;
  // text-align: center;
  border-radius: 4px;
  line-height: 30px;
  cursor: pointer;
  // margin: 4px 10px;
  position: relative;

  ${media.tablet`
    // margin: 6px 10px;
  `}

  &:hover {
    box-shadow: 0 0 0 2px var(--primary-main-color);
  }

  ${(props) =>
    props.isSelected &&
    css`
      background-color: var(--primary-main-color);
      color: white;
      font-weight: bold;
      &:after {
        display: block;
      }
    `}
`;

const TimeFrames = styled.div`
  // overflow: auto;
  flex-wrap: wrap;
  margin: 16px 0 2px 8px;
  display: flex;
  flex-grow: 1;
  justify-self: center;
  gap: 8px;
  max-width: 324px;
  // text-align: center;
`;

const SchedueleContentWrap = styled.div`
  display: flex;
  align-items: flex-start;
  overflow: hidden;
  flex-direction: column;

  > div:nth-child(1) {
    width: 100%;
  }
  max-height: 258px;

  .ant-picker-panel, .ant-picker-date-panel{
  }
  ${media.tablet`
    flex-direction: row;

    > div:nth-child(1) {
      width: auto;
      flex-grow: 1;
    }

    > div:nth-child(2) {
      height: 100%;
      margin: 0;
      display: flex;
      flex-direction: column;
    }
  `}
`;

export function TimePicker({ value, onChange, open: isOpen }) {
  const invalidTimes = useFetcher({ dataFetcher: invalidTimeFetcher });
  const openedTimes = useFetcher({ dataFetcher: openTimeFetcher });
  const selectedDate = moment(appointmentForm.state.values.date);
  const shortDays = useMemo(() => {
    if (!invalidTimes.data) {
      return [];
    }
    return invalidTimes.data.map((time) => ({
      date: moment(time.checkDay),
      scheduele: time.open_close_time,
      reason: time.reason,
    }));
  }, [invalidTimes.data]);

  const shortDayHours = useMemo(() => {
    const dayMeta = shortDays.find((shortDay) =>
      selectedDate.isSame(shortDay.date, "day")
    );
    return dayMeta?.scheduele;
  }, [selectedDate]);

  const regularHours = useMemo(() => {
    const day = selectedDate.isoWeekday();
    return openedTimes?.data?.[DAYS_OF_WEEK[day]];
  });
  const openedInterval = shortDayHours ? shortDayHours : regularHours;

  const [min, max] = openedInterval?.split("-") || [];

  const hours = useMemo(() => {
    if (!min || !max) {
      return [];
    }

    return Array.from({ length: 48 })
      .map((_, index) => {
        // If index is even.
        if (index % 2 === 0) {
          return `${index < 20 ? `0${index / 2}` : index / 2}:00`;
        } else {
          return `${index < 20 ? `0${(index - 1) / 2}` : (index - 1) / 2}:30`;
        }
      })
      .filter((time) => {
        let [hour, minutes] = time.split(":");
        let [hMin, mMin] = min.split(":");
        let [hMax, mMax] = max.split(":");

        [hour, hMin, hMax] = [hour, hMin, hMax].map((hour) =>
          parseInt(hour, 10)
        );

        hour = hour + minutes / 60;
        hMin = hMin + mMin / 60;
        hMax = hMax + mMax / 60;

        return hour >= hMin && hour < hMax;
      });
  }, [min, max]);

  const { state } = useFormContext();
  const [open, setOpen] = useState(isOpen || false);
  const selectedTime = state.values.time;
  const subHeader = selectedTime ? selectedTime : "Niet geselecteerd";

  return (
    <PanelToggle
      header={<SectionHeader title="Tijd" />}
      expandable={false}
      open={open}
      onToggle={setOpen}
    >
      <Row type="flex" style={{ justifyContent: "center" }}>
        <Col>
          <TimeFrames>
            {hours.map((hour, index) => {
              const isSelected = value === hour;
              return (
                <TimeOption
                  key={index}
                  isSelected={isSelected}
                  className="time-option"
                  onClick={() => onChange(hour)}>
                  <span>{hour}</span>
                </TimeOption>
              );
            })}
          </TimeFrames>
        </Col>
      </Row>
    </PanelToggle>
  );
}
export default function DateAndTime({ open: isOpen }) {
  const { state } = useFormContext();
  const [open, setOpen] = useState(isOpen || false);
  const selectedDate = state.values.date;
  const subHeader = selectedDate
    ? moment(selectedDate).format("dddd, DD MMMM")
    : "Not Selected";
  return (
    <PanelToggle
      header={<SectionHeader title="Datum" />}
      expandable={false}
      open={open}
      onToggle={setOpen}

    >
      <Row type="flex" style={{ justifyContent: "center" }}>
        <Col>
          <DatePickerWrap>
            <SchedueleContentWrap className="calendar-wrap">
              <Field
                className="calendar-field"
                name="date"
                as={CalendarField}
                onChange={(value) => {
                  appointmentForm.actions.onFieldChange({ name: "date", value });
                  appointmentForm.actions.onFieldChange({
                    name: "time",
                    value: "",
                  });
                }}
                css={`
              & .ant-select.ant-fullcalendar-year-select.ant-select-sm {
                background: papayawhip;
              }
            `}
              />
            </SchedueleContentWrap>
          </DatePickerWrap>
        </Col>
      </Row>
    </PanelToggle>
  );
}
